/** Per-website settings: business context, crawl configuration, automation. */

import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import { siteApi } from '@/api/endpoints';
import { useAuth } from '@/app/auth';
import { Permission } from '@/app/permissions';
import {
  Button,
  Callout,
  Card,
  CardHeader,
  Field,
  LoadingState,
  PageHeader,
} from '@/components/ui';
import {
  AlertIcon,
  CheckCircleIcon,
  InfoIcon,
  RefreshIcon,
  ShieldIcon,
} from '@/components/icons';
import { inputClass } from '@/utils/format';

export function SiteSettingsPage() {
  const { siteId = '' } = useParams();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const canEdit = can(Permission.SITE_UPDATE);

  const query = useQuery({ queryKey: ['site', siteId], queryFn: () => siteApi.get(siteId) });

  const [form, setForm] = useState({
    name: '',
    target_country: '',
    business_description: '',
    products_services: '',
    target_keywords: '',
    crawl_max_pages: 500,
    crawl_max_depth: 5,
    crawl_respect_robots: true,
    auto_approve_enabled: false,
    auto_approve_max_risk: 'LOW',
  });
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const site = query.data;
    if (!site) return;
    setForm({
      name: site.name,
      target_country: site.target_country ?? '',
      business_description: site.business_description ?? '',
      products_services: (site.products_services ?? []).join(', '),
      target_keywords: (site.target_keywords ?? []).join(', '),
      crawl_max_pages: site.crawl_max_pages,
      crawl_max_depth: site.crawl_max_depth,
      crawl_respect_robots: site.crawl_respect_robots,
      auto_approve_enabled: site.auto_approve_enabled,
      auto_approve_max_risk: site.auto_approve_max_risk ?? 'LOW',
    });
  }, [query.data]);

  const mutation = useMutation({
    mutationFn: () =>
      siteApi.update(siteId, {
        name: form.name,
        target_country: form.target_country || null,
        business_description: form.business_description || null,
        products_services: splitList(form.products_services),
        target_keywords: splitList(form.target_keywords),
        crawl_max_pages: form.crawl_max_pages,
        crawl_max_depth: form.crawl_max_depth,
        crawl_respect_robots: form.crawl_respect_robots,
        auto_approve_enabled: form.auto_approve_enabled,
        auto_approve_max_risk: form.auto_approve_enabled ? form.auto_approve_max_risk : null,
      }),
    onSuccess: async () => {
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['site', siteId] });
      await queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
  });

  if (query.isLoading) return <LoadingState />;

  return (
    <div className="website-settings-workspace">
      <PageHeader title="Website settings" description={query.data?.normalized_origin} />

      <form
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          setSaved(false);
          mutation.mutate();
        }}
        className="space-y-5"
      >
        {mutation.isError ? (
          <Callout tone="danger" icon={<AlertIcon className="h-5 w-5" />}>
            {mutation.error instanceof ApiError ? mutation.error.message : 'Could not save.'}
          </Callout>
        ) : null}
        {saved ? (
          <Callout tone="success" icon={<CheckCircleIcon className="h-5 w-5" />}>
            Settings saved.
          </Callout>
        ) : null}

        <Card>
          <CardHeader
            title="Business context"
            icon={<InfoIcon className="h-4 w-4" />}
            description="This is what lets prioritisation weight commercially important pages correctly."
          />
          <div className="space-y-4">
            <Field label="Website name" htmlFor="name" required>
              <input
                id="name"
                required
                disabled={!canEdit}
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                className={inputClass}
              />
            </Field>
            <Field label="Target country" htmlFor="country" hint="Two-letter code.">
              <input
                id="country"
                maxLength={2}
                disabled={!canEdit}
                value={form.target_country}
                onChange={(event) =>
                  setForm({ ...form, target_country: event.target.value.toUpperCase() })
                }
                className={`${inputClass} w-28 uppercase`}
              />
            </Field>
            <Field label="What does this business do?" htmlFor="description">
              <textarea
                id="description"
                rows={3}
                disabled={!canEdit}
                value={form.business_description}
                onChange={(event) =>
                  setForm({ ...form, business_description: event.target.value })
                }
                className={inputClass}
              />
            </Field>
            <Field label="Products or services" htmlFor="products" hint="Comma separated.">
              <input
                id="products"
                disabled={!canEdit}
                value={form.products_services}
                onChange={(event) => setForm({ ...form, products_services: event.target.value })}
                className={inputClass}
              />
            </Field>
            <Field label="Target keywords" htmlFor="keywords" hint="Comma separated.">
              <input
                id="keywords"
                disabled={!canEdit}
                value={form.target_keywords}
                onChange={(event) => setForm({ ...form, target_keywords: event.target.value })}
                className={inputClass}
              />
            </Field>
          </div>
        </Card>

        <Card>
          <CardHeader title="Crawl settings" icon={<RefreshIcon className="h-4 w-4" />} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Maximum pages" htmlFor="max-pages" hint="Also bounded by your plan.">
              <input
                id="max-pages"
                type="number"
                min={1}
                max={50000}
                disabled={!canEdit}
                value={form.crawl_max_pages}
                onChange={(event) =>
                  setForm({ ...form, crawl_max_pages: Number(event.target.value) })
                }
                className={inputClass}
              />
            </Field>
            <Field label="Maximum depth" htmlFor="max-depth" hint="Clicks from the homepage.">
              <input
                id="max-depth"
                type="number"
                min={1}
                max={20}
                disabled={!canEdit}
                value={form.crawl_max_depth}
                onChange={(event) =>
                  setForm({ ...form, crawl_max_depth: Number(event.target.value) })
                }
                className={inputClass}
              />
            </Field>
          </div>
          <label className="mt-4 flex items-start gap-3">
            <input
              type="checkbox"
              disabled={!canEdit}
              checked={form.crawl_respect_robots}
              onChange={(event) =>
                setForm({ ...form, crawl_respect_robots: event.target.checked })
              }
              className="mt-0.5 h-4 w-4 rounded border-border-strong text-brand focus:ring-brand"
            />
            <span className="text-sm">
              <span className="font-medium text-ink">Respect robots.txt</span>
              <span className="mt-0.5 block text-ink-muted">
                Strongly recommended. Disable only for a site you own and control.
              </span>
            </span>
          </label>
        </Card>

        <Card>
          <CardHeader
            title="Automation"
            icon={<ShieldIcon className="h-4 w-4" />}
            description="By default every change waits for your approval."
          />
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              disabled={!canEdit}
              checked={form.auto_approve_enabled}
              onChange={(event) =>
                setForm({ ...form, auto_approve_enabled: event.target.checked })
              }
              className="mt-0.5 h-4 w-4 rounded border-border-strong text-brand focus:ring-brand"
            />
            <span className="text-sm">
              <span className="font-medium text-ink">Auto-approve low-risk changes</span>
              <span className="mt-0.5 block text-ink-muted">
                Meta descriptions, image alt text and internal links can be approved automatically.
                Every automatic decision is still recorded in the approval history.
              </span>
            </span>
          </label>

          {form.auto_approve_enabled ? (
            <div className="mt-4 max-w-xs">
              <Field
                label="Maximum risk to auto-approve"
                htmlFor="max-risk"
                hint="HIGH and CRITICAL always require a person, regardless of this setting."
              >
                <select
                  id="max-risk"
                  disabled={!canEdit}
                  value={form.auto_approve_max_risk}
                  onChange={(event) =>
                    setForm({ ...form, auto_approve_max_risk: event.target.value })
                  }
                  className={inputClass}
                >
                  <option value="LOW">Low risk only</option>
                  <option value="MEDIUM">Low and medium risk</option>
                </select>
              </Field>
            </div>
          ) : null}
        </Card>

        {canEdit ? (
          // Pinned to the bottom of the scrollport: the fields run past a
          // screenful, and a save button you have to go looking for is how
          // edits get abandoned half-made.
          <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border-subtle bg-surface/95 px-4 py-3 shadow-raised backdrop-blur">
            <p className="text-xs text-ink-subtle">
              Changes take effect on the next crawl and audit.
            </p>
            <Button type="submit" variant="primary" loading={mutation.isPending}>
              Save settings
            </Button>
          </div>
        ) : (
          <Callout tone="info" icon={<InfoIcon className="h-5 w-5" />}>
            Your role does not allow changing these settings.
          </Callout>
        )}
      </form>
    </div>
  );
}

function splitList(value: string): string[] {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 100);
}
